/**
 * Settings — family info, plan badge, sign-out.
 * Account-level edits (email, password) handled by /v1/auth/* endpoints;
 * this page focuses on what's reachable today.
 */
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { LogOut, Users as UsersIcon, CreditCard, Settings } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { usersApi } from '@/api/users';
import { logout } from '@/api/auth';
import { PageHeader } from './PageHeader';
import { SecurityPanel } from './SecurityPanel';
import { AppearancePanel } from './AppearancePanel';

const PLAN_LABEL: Record<string, string> = {
  starter: 'Starter',
  family: 'Family',
  family_plus: 'Family+',
};

export function SettingsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const family = useQuery({
    queryKey: ['family'],
    queryFn: () => usersApi.myFamily(),
    retry: false,
  });
  const children = useQuery({
    queryKey: ['children'],
    queryFn: () => usersApi.listChildren(),
    retry: false,
  });

  const onSignOut = async () => {
    try {
      await logout();
      // Drop all cached query data so the previous user's family / safety /
      // SOS rows never flash if the back button is pressed before a fresh
      // login happens.
      queryClient.clear();
      toast.success('Signed out.');
      navigate('/login', { replace: true });
    } catch {
      toast.error('Could not sign out — try again.');
    }
  };

  return (
    <div className="px-6 py-8 md:px-10 md:py-10">
      <PageHeader
        title={t('pages.settings.title')}
        icon={Settings}
        tint="slate"
        description={t('pages.settings.description')}
      />

      <div className="mt-8 grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <UsersIcon className="size-4" />
              Family
            </CardTitle>
            <CardDescription>The household this dashboard belongs to.</CardDescription>
          </CardHeader>
          <CardContent>
            {family.isPending && (
              <div className="space-y-2">
                <Skeleton className="h-4 w-1/2" />
                <Skeleton className="h-4 w-2/3" />
              </div>
            )}
            {family.isError && (
              <p className="text-sm text-[color:var(--kz-color-ink-secondary)]">
                No family yet. Add a child from the Children page to create one.
              </p>
            )}
            {family.data && (
              <dl className="grid gap-2 text-sm">
                <div className="flex justify-between gap-3">
                  <dt className="text-[color:var(--kz-color-ink-muted)]">Name</dt>
                  <dd className="font-medium">{family.data.name}</dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt className="text-[color:var(--kz-color-ink-muted)]">Children</dt>
                  <dd>{children.data?.length ?? 0}</dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt className="text-[color:var(--kz-color-ink-muted)]">Created</dt>
                  <dd>{new Date(family.data.created_at).toLocaleDateString()}</dd>
                </div>
              </dl>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <CreditCard className="size-4" />
              Plan
            </CardTitle>
            <CardDescription>Subscription tier and entitlements.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-2xl font-semibold">
                  {PLAN_LABEL[family.data?.plan ?? 'starter'] ?? 'Starter'}
                </p>
                <p className="mt-0.5 text-xs text-[color:var(--kz-color-ink-muted)]">
                  Safety basics (SOS, live location, alerts) included on every plan.
                </p>
              </div>
              <Button variant="outline" disabled>
                Manage (coming soon)
              </Button>
            </div>
          </CardContent>
        </Card>

        <div className="md:col-span-2">
          <AppearancePanel />
        </div>

        <div className="md:col-span-2">
          <SecurityPanel />
        </div>

        <Card className="md:col-span-2">
          <CardHeader>
            <CardTitle className="text-base">Account</CardTitle>
            <CardDescription>Sign out of this device. Other paired children stay paired.</CardDescription>
          </CardHeader>
          <CardContent>
            <Button variant="outline" onClick={onSignOut}>
              <LogOut className="size-4" />
              Sign out
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
